import { useState, useEffect } from 'react';
import axios from '@/common/infrastructure/axios';
import { RcaClientContractor, ClientOption } from '../types/HomeTypes';
import { getClientCurrency } from '@/common/application/utils/clientCurrencyUtils';
import { getAchSurchargeIsVariable } from '@/common/application/utils/surchargeUtils';

// Helper function to parse ClientSettings JSON string
const parseClientSettings = (clientSettingsJson?: string) => {
  if (!clientSettingsJson) {
    return {
      achSurcharge: undefined,
      achSurchargeCap: undefined,
      achSurchargeIsVariable: undefined,
      creditCardSurcharge: undefined,
      cultureInfoName: undefined,
    };
  }

  try {
    const settings = JSON.parse(clientSettingsJson);
    return {
      achSurcharge: settings.ACHSurcharge,
      achSurchargeCap: settings.ACHSurchargeCap,
      achSurchargeIsVariable: getAchSurchargeIsVariable(settings.ACHSurchargeIsVariable),
      creditCardSurcharge: settings.CreditCardSurcharge,
      cultureInfoName: settings.CultureInfoName,
    };
  } catch (error) {
    console.warn('Failed to parse ClientSettings JSON:', error, 'Raw value:', clientSettingsJson);
    return {
      achSurcharge: undefined,
      achSurchargeCap: undefined,
      achSurchargeIsVariable: undefined,
      creditCardSurcharge: undefined,
      cultureInfoName: undefined,
    };
  }
};

// Cache key for storing clients in localStorage
const CLIENTS_CACHE_KEY = 'cachedClients';
const CLIENTS_CACHE_TIMESTAMP_KEY = 'cachedClientsTimestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Helper functions for clients caching
const getCachedClients = (): ClientOption[] | null => {
  try {
    const cached = localStorage.getItem(CLIENTS_CACHE_KEY);
    const timestamp = localStorage.getItem(CLIENTS_CACHE_TIMESTAMP_KEY);

    if (cached && timestamp) {
      const cacheAge = Date.now() - parseInt(timestamp, 10);
      if (cacheAge < CACHE_DURATION) {
        console.log('Loading clients from cache');
        return JSON.parse(cached);
      } else {
        console.log('Clients cache expired, will fetch fresh data');
        // Clear expired cache
        localStorage.removeItem(CLIENTS_CACHE_KEY);
        localStorage.removeItem(CLIENTS_CACHE_TIMESTAMP_KEY);
      }
    }
    return null;
  } catch (error) {
    console.warn('Failed to read clients from cache:', error);
    return null;
  }
};

const setCachedClients = (clients: ClientOption[]): void => {
  try {
    localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify(clients));
    localStorage.setItem(CLIENTS_CACHE_TIMESTAMP_KEY, Date.now().toString());
    console.log('Clients cached successfully');
  } catch (error) {
    console.warn('Failed to cache clients:', error);
  }
};

// Utility function to clear all client-related cache (useful for logout)
export const clearClientsCache = (): void => {
  try {
    localStorage.removeItem(CLIENTS_CACHE_KEY);
    localStorage.removeItem(CLIENTS_CACHE_TIMESTAMP_KEY);
    console.log('Clients cache cleared');
  } catch (error) {
    console.warn('Failed to clear clients cache:', error);
  }
};

export const useGetClients = (userID?: number) => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userID) {
      // Stay in loading until userID is available. Setting loading=false with an empty list
      // made ClientContext treat "session not ready" as "user has zero clients" and wipe the
      // persisted selectedClient (then auto-pick the first company on the real load).
      setClients([]);
      setLoading(true);
      return;
    }

    // First, try to load from cache
    const cachedClients = getCachedClients();
    if (cachedClients) {
      setClients(cachedClients);
      setLoading(false); // Important: Set loading to false when using cached data
      return; // Exit early if we have valid cached data
    }

    // If no cache or cache expired, fetch from API
    const fetchClients = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(`Fetching clients from API for userID: ${userID}`);
        const response = await axios.get<RcaClientContractor[]>(`users/${userID}/clients`);

        // Transform the response to ClientOption format for dropdown
        const clientOptions: ClientOption[] = response.data.map((item) => {
          const clientSettings = parseClientSettings(item.ClientSettings);

          // Create base client object with all Client and Contractor fields
          const baseClient: ClientOption = {
            // Core identification fields
            id: item.Client.ID,
            name: item.Client.Name,
            displayName: item.DisplayName || item.Client.Name,
            contractorName: item.Contractor.Name,
            contractorId: item.Contractor.ID,
            contractorEmail: item.Contractor.Email,
            contractorPhone: item.Contractor.Phone,
            contractorClientPersonID: item.Contractor.ClientPersonID,

            // Client address and contact information
            clientStreet: item.Client.Street,
            clientCity: item.Client.City,
            clientState: item.Client.State,
            clientZipCode: item.Client.ZipCode,
            clientCountry: item.Client.Country,
            clientEmail: item.Client.Email,
            clientPhone: item.Client.Phone,

            // Contractor address and contact information
            contractorStreet: item.Contractor.Street,
            contractorCity: item.Contractor.City,
            contractorState: item.Contractor.State,
            contractorZipCode: item.Contractor.ZipCode,
            contractorCountry: item.Contractor.Country,
            contractorType: item.Contractor.Type,

            globalUserMappingID: item.MappingID,

            // RemoveAccessList
            removeaccesslist: item.RemoveAccessList,
            permissions: item.Permissions,
            roleID: item.RoleID ?? null,
            isActive: item.IsActive ?? false,

            // Payment and culture settings
            ...clientSettings,
          };

          // Resolve currency from culture info and add it to the client object
          const currency = getClientCurrency(baseClient);

          return {
            ...baseClient,
            currency,
          };
        });

        console.log(`Successfully loaded ${clientOptions.length} clients from API`);
        setClients(clientOptions);

        // Cache the fresh data
        setCachedClients(clientOptions);
      } catch (err: any) {
        console.error('Error fetching clients:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch clients');
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [userID]);

  // Function to manually refresh clients (clears cache and fetches fresh data)
  const refreshClients = async () => {
    if (!userID) return;

    // Clear cache
    localStorage.removeItem(CLIENTS_CACHE_KEY);
    localStorage.removeItem(CLIENTS_CACHE_TIMESTAMP_KEY);

    try {
      setLoading(true);
      setError(null);

      console.log(`Manually refreshing clients for userID: ${userID}`);
      const response = await axios.get<RcaClientContractor[]>(`users/${userID}/clients`);

      // Transform the response to ClientOption format for dropdown
      const clientOptions: ClientOption[] = response.data.map((item) => {
        const clientSettings = parseClientSettings(item.ClientSettings);

        // Create base client object with all Client and Contractor fields
        const baseClient: ClientOption = {
          // Core identification fields
          id: item.Client.ID,
          name: item.Client.Name,
          displayName: item.DisplayName || item.Client.Name,
          contractorName: item.Contractor.Name,
          contractorId: item.Contractor.ID,
          contractorEmail: item.Contractor.Email,
          contractorPhone: item.Contractor.Phone,
          contractorClientPersonID: item.Contractor.ClientPersonID,

          // Client address and contact information
          clientStreet: item.Client.Street,
          clientCity: item.Client.City,
          clientState: item.Client.State,
          clientZipCode: item.Client.ZipCode,
          clientCountry: item.Client.Country,
          clientEmail: item.Client.Email,
          clientPhone: item.Client.Phone,

          // Contractor address and contact information
          contractorStreet: item.Contractor.Street,
          contractorCity: item.Contractor.City,
          contractorState: item.Contractor.State,
          contractorZipCode: item.Contractor.ZipCode,
          contractorCountry: item.Contractor.Country,
          contractorType: item.Contractor.Type,
          
          globalUserMappingID: item.MappingID,

          // RemoveAccessList
          removeaccesslist: item.RemoveAccessList,
          permissions: item.Permissions,
          roleID: item.RoleID ?? null,
          isActive: item.IsActive ?? false,

          // Payment and culture settings
          ...clientSettings,
        };

        // Resolve currency from culture info and add it to the client object
        const currency = getClientCurrency(baseClient);

        return {
          ...baseClient,
          currency,
        };
      });

      console.log(`Successfully refreshed ${clientOptions.length} clients`);
      setClients(clientOptions);

      // Cache the fresh data
      setCachedClients(clientOptions);
    } catch (err: any) {
      console.error('Error refreshing clients:', err);
      setError(err.response?.data?.message || err.message || 'Failed to refresh clients');
    } finally {
      setLoading(false);
    }
  };

  return {
    clients,
    loading,
    error,
    refreshClients,
  };
};
